"use client";

import {
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  ArgandDiagram,
  type ArgandPoint,
} from "@/components/tools/algebra/argand-diagram";
import {
  StepList,
  type WorkedStep,
} from "@/components/tools/algebra/step-list";
import type { COMPLEX_NUMBER_CALCULATOR_STRINGS } from "@/components/tools/complex-number-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type Complex,
  cAdd,
  cConj,
  cDiv,
  cMul,
  cPow,
  cSub,
  formatComplex,
  formatPiFraction,
  normSquared,
  nthRoots,
  positionOf,
  toDegrees,
  toPolar,
} from "@/lib/tool-kit/algebra/complex";
import {
  formatRadicalFraction,
  sqrtOfFraction,
} from "@/lib/tool-kit/math/radical";
import {
  formatStyled,
  mulR,
  type NumberStyle,
  negR,
  type Rational,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

type Strings = typeof COMPLEX_NUMBER_CALCULATOR_STRINGS;
type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

export interface ComplexContext {
  strings: Strings;
  style: NumberStyle;
  mark: string;
}

export type ArithmeticOperation = "add" | "subtract" | "multiply" | "divide";

const ZERO_LIMIT = 5e-13;

function dec(fmt: Format, value: number): string {
  const v = Math.abs(value) < ZERO_LIMIT ? 0 : value;
  return fmt(v, { maximumFractionDigits: 4 }).replace("-", "−");
}

/** A float complex number as `3 − 4i` with at most four decimals. */
export function decimalComplex(fmt: Format, re: number, im: number): string {
  const imaginary = Math.abs(im) < ZERO_LIMIT ? 0 : im;
  const real = Math.abs(re) < ZERO_LIMIT ? 0 : re;
  if (imaginary === 0) {
    return dec(fmt, real);
  }
  const magnitude = dec(fmt, Math.abs(imaginary));
  const part = `${magnitude === "1" ? "" : magnitude}i`;
  if (real === 0) {
    return `${imaginary < 0 ? "−" : ""}${part}`;
  }
  return `${dec(fmt, real)} ${imaginary < 0 ? "−" : "+"} ${part}`;
}

const rect = (z: Complex, c: ComplexContext) =>
  formatComplex(z, c.style, c.mark);
const num = (v: Rational, c: ComplexContext) =>
  formatStyled(v, c.style, c.mark);
const br = (text: string) =>
  text.startsWith("−") || text.includes("/") ? `(${text})` : text;

function radicalText(norm: Rational): { exact: string; irrational: boolean } {
  const root = sqrtOfFraction(norm.num, norm.den);
  return {
    exact: formatRadicalFraction(root),
    irrational: root.radicand !== BigInt(1),
  };
}

function modulusLine(z: Complex, c: ComplexContext, fmt: Format): string {
  const norm = normSquared(z);
  const { exact, irrational } = radicalText(norm);
  const n = num(norm, c);
  const root = `√${n.includes("/") ? `(${n})` : n}`;
  const parts = [
    `|z| = √(${br(num(z.re, c))}² + ${br(num(z.im, c))}²) = ${root}`,
  ];
  if (exact !== root) {
    parts.push(exact);
  }
  if (irrational) {
    parts.push(`≈ ${dec(fmt, Math.sqrt(toNumberR(norm)))}`);
  }
  return parts.join(" = ").replace("= ≈", "≈");
}

function ArithmeticSteps({
  a,
  b,
  op,
  result,
  c,
}: {
  a: Complex;
  b: Complex;
  op: ArithmeticOperation;
  result: Complex;
  c: ComplexContext;
}) {
  const { strings } = c;
  const [p, q, r, s] = [a.re, a.im, b.re, b.im].map((v) => num(v, c));
  const steps: WorkedStep[] = [];
  if (op === "add" || op === "subtract") {
    const sign = op === "add" ? "+" : "−";
    steps.push(
      {
        text: strings.stepFormula,
        math: `(a + bi) ${sign} (c + di) = (a ${sign} c) + (b ${sign} d)i`,
      },
      {
        text: strings.stepSubstitute,
        math: `(${p} ${sign} ${br(r)}) + (${q} ${sign} ${br(s)})i = ${rect(result, c)}`,
      }
    );
  } else if (op === "multiply") {
    steps.push(
      {
        text: strings.stepFormula,
        math: "(a + bi)(c + di) = (ac − bd) + (ad + bc)i",
      },
      {
        text: strings.stepSubstitute,
        math: `(${br(p)} × ${br(r)} − ${br(q)} × ${br(s)}) + (${br(p)} × ${br(s)} + ${br(q)} × ${br(r)})i`,
      },
      {
        text: strings.stepEvaluate,
        math: `(${num(mulR(a.re, b.re), c)} − ${br(num(mulR(a.im, b.im), c))}) + (${num(mulR(a.re, b.im), c)} + ${br(num(mulR(a.im, b.re), c))})i = ${rect(result, c)}`,
      }
    );
  } else {
    const top = cMul(a, cConj(b));
    const bottom = normSquared(b);
    steps.push(
      {
        text: strings.stepConjugate,
        math: `(${rect(a, c)})(${rect(cConj(b), c)}) / (${rect(b, c)})(${rect(cConj(b), c)})`,
      },
      {
        text: strings.stepDenominator,
        math: `${br(r)}² + ${br(s)}² = ${num(bottom, c)}`,
      },
      {
        text: strings.stepNumerator,
        math: `(${rect(a, c)})(${rect(cConj(b), c)}) = ${rect(top, c)}`,
      },
      {
        text: strings.stepDivideParts,
        math: `(${rect(top, c)}) / ${num(bottom, c)} = ${rect(result, c)}`,
      }
    );
  }
  return <StepList steps={steps} />;
}

function combine(
  op: ArithmeticOperation,
  a: Complex,
  b: Complex
): Complex | null {
  switch (op) {
    case "add":
      return cAdd(a, b);
    case "subtract":
      return cSub(a, b);
    case "multiply":
      return cMul(a, b);
    default:
      return cDiv(a, b);
  }
}

export function ArithmeticView({
  a,
  b,
  op,
  c,
}: {
  a: Complex;
  b: Complex;
  op: ArithmeticOperation;
  c: ComplexContext;
}) {
  const fmt = useToolNumber();
  const { strings } = c;
  const result = combine(op, a, b);
  if (result === null) {
    return (
      <p className="text-muted-foreground text-sm">{strings.divideByZero}</p>
    );
  }
  const polar = toPolar(result);
  const reF = toNumberR(result.re);
  const imF = toNumberR(result.im);
  const points: ArgandPoint[] = [
    { re: toNumberR(a.re), im: toNumberR(a.im), label: "z₁" },
    { re: toNumberR(b.re), im: toNumberR(b.im), label: "z₂" },
    { re: reF, im: imF, label: strings.resultPoint },
  ];
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.result} value={rect(result, c)} />
        <StatCard
          label={strings.decimal}
          value={decimalComplex(fmt, reF, imF)}
        />
        <StatCard
          label={strings.modulus}
          value={dec(fmt, Math.hypot(reF, imF))}
        />
        <StatCard
          label={strings.argument}
          value={
            polar
              ? `${dec(fmt, toDegrees(polar.argument))}°`
              : strings.undefinedArgument
          }
        />
      </StatGrid>
      <h3 className="font-semibold text-foreground text-lg">
        {strings.workingHeading}
      </h3>
      <ArithmeticSteps a={a} b={b} c={c} op={op} result={result} />
      <h3 className="font-semibold text-foreground text-lg">
        {strings.planeHeading}
      </h3>
      <ArgandDiagram
        downloadLabel={strings.downloadPng}
        imaginaryLabel="Im"
        points={points}
        realLabel="Re"
        title={strings.planeTitle}
      />
    </>
  );
}

function angleText(
  polar: NonNullable<ReturnType<typeof toPolar>>,
  fmt: Format
): string {
  return polar.exact
    ? formatPiFraction(polar.exact.p, polar.exact.q)
    : `${dec(fmt, toDegrees(polar.argument))}°`;
}

export function PolarView({ z, c }: { z: Complex; c: ComplexContext }) {
  const fmt = useToolNumber();
  const { strings } = c;
  const polar = toPolar(z);
  if (polar === null) {
    return <p className="text-muted-foreground text-sm">{strings.zeroPolar}</p>;
  }
  const norm = normSquared(z);
  const { exact, irrational } = radicalText(norm);
  const radius = exact;
  const angle = angleText(polar, fmt);
  const radians = polar.exact
    ? formatPiFraction(polar.exact.p, polar.exact.q)
    : dec(fmt, polar.argument);
  const degrees = `${dec(fmt, toDegrees(polar.argument))}°`;
  const inverse = cDiv(
    {
      re: { num: BigInt(1), den: BigInt(1) },
      im: { num: BigInt(0), den: BigInt(1) },
    },
    z
  );
  const steps: WorkedStep[] = [
    { text: strings.stepModulus, math: modulusLine(z, c, fmt) },
    {
      text: t(strings.stepArgument, {
        position: strings.positions[positionOf(z)],
      }),
      math: polar.exact
        ? `θ = ${radians} = ${degrees}`
        : `θ = atan2(${num(z.im, c)}, ${num(z.re, c)}) = ${dec(fmt, polar.argument)} rad = ${degrees}`,
    },
    {
      text: strings.stepPolar,
      math: `z = r(cos θ + i sin θ) = ${radius}(cos ${angle} + i sin ${angle})`,
    },
    {
      text: strings.stepExponential,
      math: `z = re^(iθ) = ${radius} × e^(i × ${radians})`,
    },
  ];
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.modulus}
          value={irrational ? `${exact} ≈ ${dec(fmt, polar.modulus)}` : exact}
        />
        <StatCard label={strings.argumentRadians} value={`${radians}`} />
        <StatCard label={strings.argumentDegrees} value={degrees} />
        <StatCard label={strings.conjugate} value={rect(cConj(z), c)} />
        <StatCard
          label={strings.reciprocal}
          value={inverse ? rect(inverse, c) : "—"}
        />
        <StatCard
          label={strings.opposite}
          value={rect({ re: negR(z.re), im: negR(z.im) }, c)}
        />
      </StatGrid>
      <h3 className="font-semibold text-foreground text-lg">
        {strings.workingHeading}
      </h3>
      <StepList steps={steps} />
      <p className="text-muted-foreground text-sm">{strings.argumentNote}</p>
      <h3 className="font-semibold text-foreground text-lg">
        {strings.planeHeading}
      </h3>
      <ArgandDiagram
        downloadLabel={strings.downloadPng}
        imaginaryLabel="Im"
        points={[{ re: toNumberR(z.re), im: toNumberR(z.im), label: "z" }]}
        realLabel="Re"
        title={strings.planeTitle}
      />
    </>
  );
}

export function PowerRootView({
  z,
  n,
  c,
}: {
  z: Complex;
  n: number;
  c: ComplexContext;
}) {
  const fmt = useToolNumber();
  const { strings } = c;
  const polar = toPolar(z);
  const power = cPow(z, n);
  const roots = nthRoots(z, n);
  if (polar === null || power === null || roots === null) {
    return <p className="text-muted-foreground text-sm">{strings.zeroPolar}</p>;
  }
  const degrees = toDegrees(polar.argument);
  const nTheta = degrees * n;
  const rn = polar.modulus ** n;
  const check = decimalComplex(
    fmt,
    rn * Math.cos(n * polar.argument),
    rn * Math.sin(n * polar.argument)
  );
  const steps: WorkedStep[] = [
    {
      text: strings.stepExact,
      math: `(${rect(z, c)})^${n} = ${rect(power, c)}`,
    },
    {
      text: strings.stepDeMoivre,
      math: `z^n = r^n (cos nθ + i sin nθ) = ${dec(fmt, polar.modulus)}^${n} (cos(${n} × ${dec(fmt, degrees)}°) + i sin(${n} × ${dec(fmt, degrees)}°))\n= ${dec(fmt, rn)} (cos ${dec(fmt, nTheta)}° + i sin ${dec(fmt, nTheta)}°) ≈ ${check}`,
    },
  ];
  const radius = polar.modulus ** (1 / n);
  const points: ArgandPoint[] = roots.map((root) => ({
    re: root.re,
    im: root.im,
    label: `k=${root.index}`,
  }));
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.powerLabel, { n })}
          value={rect(power, c)}
        />
        <StatCard
          label={strings.powerDecimal}
          value={decimalComplex(fmt, toNumberR(power.re), toNumberR(power.im))}
        />
        <StatCard label={strings.rootRadius} value={dec(fmt, radius)} />
      </StatGrid>
      <h3 className="font-semibold text-foreground text-lg">
        {t(strings.powerHeading, { n })}
      </h3>
      <StepList steps={steps} />
      <h3 className="font-semibold text-foreground text-lg">
        {t(strings.rootsHeading, { n })}
      </h3>
      <p className="text-muted-foreground text-sm">{strings.rootsIntro}</p>
      <DataTable
        head={[strings.rootIndex, strings.rootAngle, strings.rootValue]}
        rows={roots.map((root) => [
          root.index,
          root.exact
            ? formatPiFraction(root.exact.p, root.exact.q)
            : `${dec(fmt, toDegrees(root.angle))}°`,
          decimalComplex(fmt, root.re, root.im),
        ])}
      />
      <ArgandDiagram
        downloadLabel={strings.downloadPng}
        imaginaryLabel="Im"
        points={points}
        polygon={n > 2}
        radius={radius}
        realLabel="Re"
        title={t(strings.rootsPlaneTitle, { n })}
      />
    </>
  );
}
